import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Error, MoreVert, Warning } from '@mui/icons-material';
import { AppBar, Box, IconButton, Menu, styled, Tab, Tabs, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { useHotKey } from 'common/hooks';
import { HORIZONTAL_NAVIGATION_Z_INDEX } from 'components/HorizontalNavigation';
import { useZoom } from 'context/ZoomContext';
import { useApproachQueryParam } from 'pages/ValuationsAllocation/hooks';
import { HEADER_HEIGHT, PROGRESS_BAR_HEIGHT, SIDEBAR_CLOSED_WIDTH } from 'theme';
import { getBooleanValue, getNumberValue, getStringValue, textWithEllipsisStyle } from 'utilities';
import { useChildrenActive } from './hooks';
import useStyles from './styles';
import { TabContentProps, TabsNavigationProps, Tab as TabType } from './types';

const TABS_MIN_HEIGHT = 40;

const StyledRootGrid = styled(Grid)<{ relativeDrawer: boolean }>(({ relativeDrawer, theme }) => ({
  height: relativeDrawer ? 'calc(100% - 1rem)' : '100%',
  marginTop: relativeDrawer ? theme.spacing(4.5) : 0,
  width: relativeDrawer ? 'calc(100% - 1rem)' : '100%',
}));

const StyledAppBar = styled(AppBar)<{
  relativeDrawer: boolean;
  sidePanelOpen: boolean;
  zoomLevel: number;
}>(({ zoomLevel, theme }) => ({
  left: `${SIDEBAR_CLOSED_WIDTH * zoomLevel}px`,
  marginTop: 0,
  paddingLeft: 0,
  top: `calc(${HEADER_HEIGHT * zoomLevel}px - ${PROGRESS_BAR_HEIGHT}px)`,
  width: `calc(100% - ${SIDEBAR_CLOSED_WIDTH * zoomLevel}px)`,
  zIndex: HORIZONTAL_NAVIGATION_Z_INDEX - 2,
  zoom: 1 / zoomLevel,

  '&.MuiAppBar-positionAbsolute': {
    marginTop: theme.spacing(4.5),
    paddingLeft: theme.spacing(2),
    position: 'absolute',
    right: theme.spacing(2),
    top: `-${theme.spacing(1)}`,
    width: 'auto',
  },

  // Scroll Buttons
  '& .MuiTabs-scrollButtons': {
    width: `calc(40px * ${zoomLevel})`,
  },
}));

const StyledTabsContainer = styled(Tabs)<{ zoomLevel: number }>(({ zoomLevel }) => ({
  minHeight: `calc(${TABS_MIN_HEIGHT}px * ${zoomLevel})`,

  '& .MuiTabs-indicator': {
    display: 'none',
  },
}));

const StyledTab = styled(Tab)<{ zoomLevel: number }>(({ theme, zoomLevel }) => ({
  color: theme.palette.primary.main,
  maxWidth: 'none',
  minHeight: `calc(${TABS_MIN_HEIGHT}px * ${zoomLevel})`,
  minWidth: 'auto',
  padding: `calc(${theme.spacing(0.5)} * ${zoomLevel}) calc(${theme.spacing(1.5)} * ${zoomLevel})`,
  position: 'relative',

  // underline pseudo-element exists in both states to allow smooth transitions
  '&::after': {
    content: '""',
    backgroundColor: theme.palette.primary.main,
    bottom: 0,
    height: '3px',
    left: 0,
    position: 'absolute',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 200ms ease',
    width: '100%',
  },

  '&.Mui-selected': {
    backgroundColor: theme.palette.common.white,

    '&::after': {
      height: `calc(3px * ${zoomLevel})`,
      transform: 'scaleX(1)',
    },
  },
}));

const StyledLabelContainer = styled(Grid)<{ zoomLevel: number }>(({ theme, zoomLevel }) => ({
  alignItems: 'center',
  display: 'flex',
  overflow: 'hidden',

  '& .MuiButton-root': {
    fontSize: `calc(${theme.spacing(2)} * ${zoomLevel})`,
    lineHeight: 1,
    minWidth: `calc(${theme.spacing(6.25)} * ${zoomLevel})`,
  },
}));

const StyledTypographyLabel = styled(Typography)<{ zoomLevel: number }>(({ theme, zoomLevel }) => ({
  ...textWithEllipsisStyle,
  color: theme.palette.grey[400],
  fontSize: `calc(${theme.spacing(2)} * ${zoomLevel})`,
  fontWeight: 700,
  lineHeight: 1,

  '&.active': {
    color: theme.palette.grey[900],
  },

  '&.hovered': {
    color: theme.palette.grey[700],
  },
}));

const NAVIGATION_TABS_IDENTIFIER = 'navigations-tabs';

const TabContent = ({ content = null, isActive, mountOnActive, openedTabs, tabIndex }: TabContentProps) => {
  const TabContentElement = <>{content}</>;

  // Render content on Mount all Tabs (slower performance at first load)
  if (!mountOnActive) {
    return TabContentElement;
  }

  // Render only Active Tab's Content and Opened Tabs
  if (isActive || openedTabs?.get(getNumberValue(tabIndex))) {
    return TabContentElement;
  }

  return null;
};

const TabsNavigation = ({
  appBarSx,
  children,
  handleMenuClose,
  handleMenuOpen,
  invalidTables,
  isDisabled = false,
  menuAnchorEl,
  mountOnActive = false,
  relativeDrawer = false,
  rightAction,
  sidePanelOpen = false,
  tabs = [],
  updateUrlOnTabChange = false,
  activeTabId,
  onActiveTabIdApplied,
  onActiveTabChange,
  controlledActiveTab,
}: TabsNavigationProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [hoveredTab, setHoveredTab] = useState<number | null>(null);
  const [openedTabs, setOpenedTabs] = useState<Map<number, boolean>>(new Map());
  const [menuTabIndex, setMenuTabIndex] = useState<number | null>(null);
  const lastNotifiedActiveTabIdRef = useRef<string | null>(null);

  const { zoomLevel } = useZoom();

  const handleActiveTabChange = useCallback(
    (tab: TabType) => {
      onActiveTabChange?.(tab);
    },
    [onActiveTabChange]
  );

  useEffect(() => {
    if (tabs) {
      setActiveTab(prev => (prev > tabs.length ? 0 : prev));
    }
  }, [tabs]);

  useEffect(() => {
    if (controlledActiveTab != null && controlledActiveTab !== activeTab && controlledActiveTab < tabs.length) {
      setActiveTab(controlledActiveTab);
    }
    // disabling as activeTab is not in the dependencies and causes rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledActiveTab, tabs.length]);

  useEffect(() => {
    const currentTab = tabs?.[activeTab];
    const currentTabId = currentTab ? getStringValue(currentTab?.id) : null;
    const shouldNotify = currentTabId !== null && currentTabId !== lastNotifiedActiveTabIdRef.current;

    if (!shouldNotify) {
      return;
    }

    lastNotifiedActiveTabIdRef.current = currentTabId;
    handleActiveTabChange(currentTab);
  }, [activeTab, handleActiveTabChange, tabs]);

  const childrenRef = useRef<HTMLDivElement | null>(null);

  const hasChildrenActive = useChildrenActive(childrenRef);

  const classes = useStyles({ hasChildrenActive, zoomLevel });

  const invalidTabs = useMemo(() => invalidTables?.map(table => getStringValue(table?.id)), [invalidTables]);

  const { tabId, setApproachQueryParam } = useApproachQueryParam();

  const moveToPreviousTab = (currentTab: number) => {
    const previousTabPosition = currentTab - 1;

    let previousTab = Math.max(0, previousTabPosition);

    // Handle disabled tabs from the current Tab to the first Tab
    while (tabs?.[previousTab]?.disabled && previousTab >= 0) {
      previousTab -= 1;
    }

    // If the first Tab is disabled, keep the current Tab active
    if (previousTab === 0 && tabs?.[previousTab]?.disabled) {
      previousTab = currentTab;
    }

    const id = getStringValue(tabs?.[previousTab]?.id);
    if (updateUrlOnTabChange) {
      setApproachQueryParam(id);
    } else {
      setActiveTab(previousTab);
    }
  };

  const moveToNextTab = (currentTab: number) => {
    const nextTabPosition = currentTab + 1;

    const tabsQuantity = getNumberValue(tabs?.length);

    let nextTab = nextTabPosition >= tabsQuantity ? tabsQuantity - 1 : nextTabPosition;

    // Handle disabled tabs from the current Tab to the last Tab
    while (tabs?.[nextTab]?.disabled && nextTab < tabsQuantity - 1) {
      nextTab += 1;
    }

    // If the last Tab is disabled, keep the current Tab active
    if (nextTab === tabsQuantity - 1 && tabs?.[nextTab]?.disabled) {
      nextTab = currentTab;
    }

    const id = getStringValue(tabs?.[nextTab]?.id);
    if (updateUrlOnTabChange) {
      setApproachQueryParam(id);
    } else {
      setActiveTab(nextTab);
    }
  };

  useHotKey({
    callback: () => moveToPreviousTab(activeTab),
    key: 'PageDown',
    pressedKeys: [],
  });

  useHotKey({
    callback: () => moveToNextTab(activeTab),
    key: 'PageUp',
    pressedKeys: [],
  });

  const handleTabChange = useCallback(() => {
    const approachTabIndex = tabs?.findIndex(tab => tab?.id === tabId) ?? -1;
    const currentTab = tabs[activeTab];

    /**
     * When the last non action tab is removed the activeTab value remains the same which will cause an action tab to be selected
     * To prevent this, when the current tab is an action tab, the last non action tab will be selected
     */
    if (currentTab?.isActionTab) {
      const lastNonActionTabIndex = tabs.findLastIndex(tab => !tab?.isActionTab && !tab?.disabled);
      setActiveTab(lastNonActionTabIndex === -1 ? 0 : lastNonActionTabIndex);
      return;
    }

    if (approachTabIndex >= 0) {
      setActiveTab(approachTabIndex);
    }
  }, [activeTab, tabId, tabs]);

  const addOpenedTab = (tabIndex: number) => {
    setOpenedTabs(prevState => new Map(prevState.set(tabIndex, true)));
  };

  const handleChange = (_event: ChangeEvent<object>, value: number) => {
    const nextTab = tabs?.[value];
    if (!nextTab) {
      return;
    }

    addOpenedTab(value);

    // Handle action tabs without touching the URL to avoid loops
    if (nextTab.isActionTab) {
      handleActiveTabChange(nextTab);
      return;
    }

    const id = getStringValue(nextTab.id);

    if (updateUrlOnTabChange) {
      setApproachQueryParam(id);
    } else {
      setActiveTab(value);
    }
  };

  const handleOnMouseEnter = (tabIndex: number) => setHoveredTab(tabIndex);
  const handleOnMouseLeave = () => setHoveredTab(null);

  const handleMenuCloseInternal = useCallback(() => {
    setMenuTabIndex(null);
    handleMenuClose?.();
  }, [handleMenuClose]);

  useEffect(() => {
    if (!menuAnchorEl) {
      setMenuTabIndex(null);
    }
  }, [menuAnchorEl]);

  // Reset the Active Tab if it becomes invalid or points to an action tab (e.g. after deleting a note)
  useEffect(() => {
    if (!tabs?.length) {
      return;
    }
    setActiveTab(prev => {
      const next = prev >= tabs.length ? Math.max(0, tabs.length - 1) : prev;
      const currentTab = tabs[next];
      if (currentTab?.isActionTab) {
        const lastNonAction = tabs.findLastIndex(tab => !tab?.isActionTab && !tab?.disabled);
        return lastNonAction === -1 ? 0 : lastNonAction;
      }
      return next;
    });
  }, [tabs]);

  // Set the Active Tab based on the Query Param
  useEffect(() => {
    if (!tabId || !tabs) {
      return;
    }

    if (updateUrlOnTabChange) {
      handleTabChange();
    }
  }, [handleTabChange, tabId, tabs, updateUrlOnTabChange]);

  // When parent requests a specific tab (e.g. after adding a note), select it then clear.
  // No-op when activeTabId is not provided (all TabsNavigation usages except NoteList omit it).
  useEffect(() => {
    if (activeTabId === null || activeTabId === undefined || !tabs?.length) {
      return;
    }
    const index = tabs.findIndex(tab => getStringValue(tab?.id) === activeTabId);
    if (index >= 0 && !tabs[index]?.isActionTab) {
      setActiveTab(index);
      addOpenedTab(index);
      onActiveTabIdApplied?.();
    }
  }, [activeTabId, onActiveTabIdApplied, tabs]);

  return (
    <StyledRootGrid id="tab-nav-grid-id" display="block" container relativeDrawer={relativeDrawer}>
      <StyledAppBar
        color="default"
        elevation={0}
        id="tab-nav-app-bar-id"
        position={relativeDrawer ? 'absolute' : 'fixed'}
        relativeDrawer={relativeDrawer}
        sidePanelOpen={sidePanelOpen}
        sx={{ ...appBarSx as object, display: 'flex', flexDirection: 'row', alignItems: 'center' }}
        zoomLevel={zoomLevel}>
        <StyledTabsContainer
          sx={{ flex: 1, minWidth: 0 }}
          aria-label={NAVIGATION_TABS_IDENTIFIER}
          indicatorColor="primary"
          onChange={handleChange}
          scrollButtons="auto"
          textColor="primary"
          value={activeTab}
          variant="scrollable"
          zoomLevel={zoomLevel}>
          {tabs?.map((tab, tabIndex) => {
            const tabIsActive = activeTab === tabIndex;
            const tabIsHovered = hoveredTab === tabIndex;

            return (
              <StyledTab
                // Accessibility props for the tabs
                aria-controls={`${NAVIGATION_TABS_IDENTIFIER}-tabpanel-${tabIndex}`}
                disabled={getBooleanValue(tab?.disabled)}
                disableRipple={tab?.disableRipple}
                id={`${NAVIGATION_TABS_IDENTIFIER}-${tabIndex}`}
                key={getStringValue(tab?.id)}
                label={
                  <Grid alignItems="center" container justifyContent="flex-end" wrap="nowrap">
                    <StyledLabelContainer zoomLevel={zoomLevel}>
                      {typeof tab.label === 'string' ? (
                        <StyledTypographyLabel
                          className={clsx({ active: tabIsActive, hovered: tabIsHovered })}
                          zoomLevel={zoomLevel || 1}>
                          {getStringValue(tab?.label)}
                        </StyledTypographyLabel>
                      ) : (
                        tab.label
                      )}

                      {tab?.disabled && <Warning className={classes.disabledIcon} fontSize="small" />}

                      {invalidTabs?.includes(getStringValue(tab?.id)) && (
                        <Error className={classes.errorIcon} fontSize="small" />
                      )}
                    </StyledLabelContainer>

                    {tab?.menuItems && tab?.menuItems?.length > 0 && (
                      <Grid
                        className={clsx(classes.iconContainer, {
                          active: tabIsHovered || tabIsActive,
                        })}>
                        <IconButton component="span" onClick={event => {
                          handleMenuOpen?.(event, tab, tabIndex);
                          setMenuTabIndex(tabIndex);
                        }} size="small" disabled={isDisabled}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                }
                onMouseEnter={() => handleOnMouseEnter(tabIndex)}
                onMouseLeave={handleOnMouseLeave}
                zoomLevel={zoomLevel}
              />
            );
          })}
        </StyledTabsContainer>
        {rightAction && (
          <Box sx={{ ml: 'auto', flexShrink: 0, pr: 1 }}>
            {rightAction}
          </Box>
        )}
      </StyledAppBar>

      <Menu
        anchorEl={menuAnchorEl}
        id={`${NAVIGATION_TABS_IDENTIFIER}-menu-${tabs?.[menuTabIndex ?? activeTab]?.id}`}
        onClose={handleMenuCloseInternal}
        open={Boolean(menuAnchorEl)}>
        {/* Menu Items */}
        {tabs?.[menuTabIndex ?? activeTab]?.menuItems?.map(MenuItemOptions => MenuItemOptions)}
      </Menu>

      {/* Render Children */}
      {children && (
        <Box ref={childrenRef} className={classes.childrenContainer}>
          {children}
        </Box>
      )}

      {/* Render the Tab Content */}
      {tabs?.map((tab, tabIndex) => {
        const tabIsActive = activeTab === tabIndex;

        return (
          <Grid
            aria-labelledby={`${NAVIGATION_TABS_IDENTIFIER}-${tabIndex}`}
            className={relativeDrawer ? '' : clsx(classes.tabPanel, { active: tabIsActive })}
            container
            // use display instead of hidden property as display will prevent the content from being rendered
            style={{ display: tabIsActive ? 'block' : 'none' }}
            id={`${NAVIGATION_TABS_IDENTIFIER}-tabpanel-${tabIndex}`}
            key={getStringValue(tab?.id)}
            role="tabpanel">
            <Box p={0}>
              <TabContent
                content={tab?.content}
                isActive={tabIsActive}
                mountOnActive={mountOnActive}
                openedTabs={openedTabs}
                tabIndex={tabIndex}
              />
            </Box>
          </Grid>
        );
      })}
    </StyledRootGrid>
  );
};

export default TabsNavigation;
